/**
 * Custom Product Gallery — now with seamless infinite loop
 * - Keeps your existing API and UI wiring
 * - Variant sync remains disabled here, handled by cpg-variant-sync.js
 */
(() => {
  const sel = (root, q) => root.querySelector(q);
  const selAll = (root, q) => Array.from(root.querySelectorAll(q));
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const isDesktop = () => matchMedia('(min-width: 1024px)').matches;
  const isTouch = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  /* ============== Slider (infinite) ============== */
  class Slider {
    constructor(root, trackSelector, { loop = true } = {}) {
      this.root = root;
      this.track = sel(root, trackSelector);
      this.loop = !!loop;

      this._initSlides(); // builds clones if loop
      this.width = 0;

      // Public logical index, 0..(count-1)
      this.index = 0;

      // Internal index in DOM, includes clones when loop=true
      this._i = this.loop ? 1 : 0;

      this.onChange = () => {};
      this.dragging = false; this.startX = 0; this.startY = 0;
      this.currentX = 0; this.deltaX = 0; this.lockY = false;

      this._resize();
      new ResizeObserver(() => this._resize()).observe(this.root);
      window.addEventListener('resize', () => this._resize(), { passive: true });
      this._bindTouch();
      this._bindSnap();
    }

    _slidesAll() { return selAll(this.track, ':scope > *'); }
    _slidesReal() { return selAll(this.track, ':scope > *:not([data-clone="true"])'); }

    _initSlides() {
      this.track.style.transform = 'translate3d(0,0,0)';
      // Cleanup any prior clones
      selAll(this.track, ':scope > *[data-clone="true"]').forEach(n => n.remove());

      this.slides = this._slidesAll();
      this.realSlides = this._slidesReal();
      this.count = this.realSlides.length;

      if (this.loop && this.count > 1) {
        const first = this.realSlides[0];
        const last = this.realSlides[this.count - 1];
        const pre = last.cloneNode(true);
        const post = first.cloneNode(true);
        pre.setAttribute('data-clone', 'true');
        post.setAttribute('data-clone', 'true');
        this.track.insertBefore(pre, this.track.firstChild);
        this.track.appendChild(post);
        this.slides = this._slidesAll();
      }
    }

    _resize() {
      this.width = this.root.clientWidth || 1;
      this._translateTo(this._i, false);
    }

    /* Compute logical index from internal index */
    _logicalFromInternal(i) {
      if (!this.loop) return clamp(i, 0, this.count - 1);
      if (i === 0) return this.count - 1;
      if (i === this.count + 1) return 0;
      return i - 1; // shift by 1 due to leading clone
    }

    /* Compute internal index from logical index */
    _internalFromLogical(li) {
      if (!this.loop) return clamp(li, 0, this.count - 1);
      return li + 1; // account for leading clone
    }

    _translateTo(i, animate = true) {
      this.track.style.transition = animate ? 'transform .25s ease' : 'none';
      this.track.style.transform = `translate3d(${-i * this.width}px,0,0)`;
      this._i = i;

      // Fire change with logical index
      this.index = this._logicalFromInternal(this._i);
      this.onChange(this.index);
    }

    goTo(li, animate = true) {
      // Public method uses logical index
      const i = this._internalFromLogical(li);
      this._translateTo(i, animate);
    }

    next() {
      const maxI = this.loop ? this.count + 1 : this.count - 1;
      const i = Math.min(this._i + 1, maxI);
      this._translateTo(i, true);
    }

    prev() {
      const minI = this.loop ? 0 : 0;
      const i = Math.max(this._i - 1, minI);
      this._translateTo(i, true);
    }

    _bindSnap() {
      if (!this.loop || this.count < 2) return;
      this.track.addEventListener('transitionend', () => {
        if (this._i === 0) {
          // jumped to leading clone, snap to real last
          this._translateTo(this.count, false);
        } else if (this._i === this.count + 1) {
          // jumped past last to trailing clone, snap to real first
          this._translateTo(1, false);
        }
      });
    }

    _bindTouch() {
      if (!isTouch()) return;
      const el = this.root;

      const onStart = (e) => {
        if (e.touches && e.touches.length > 1) return;
        this.dragging = true; this.lockY = false;
        const pt = e.touches ? e.touches[0] : e;
        this.startX = pt.clientX; this.startY = pt.clientY;
        this.currentX = this.startX; this.deltaX = 0;
        this.track.style.transition = 'none';
      };

      const onMove = (e) => {
        if (!this.dragging) return;
        if (e.touches && e.touches.length > 1) { this.dragging = false; return; }

        const pt = e.touches ? e.touches[0] : e;
        const x = pt.clientX, y = pt.clientY;
        const dx = Math.abs(x - this.startX);
        const dy = Math.abs(y - this.startY);

        if (!this.lockY) {
          const HORIZONTAL_INTENT = 1.2;
          if (dy > dx * HORIZONTAL_INTENT) {
            this.dragging = false;
            this._translateTo(this._i, false);
            return;
          }
          this.lockY = true;
        }

        this.currentX = x;
        this.deltaX = this.currentX - this.startX;
        const base = -this._i * this.width;
        this.track.style.transform = `translate3d(${base + this.deltaX}px,0,0)`;
        if (e.cancelable) e.preventDefault();
      };

      const onEnd = () => {
        if (!this.dragging) return;
        const threshold = Math.min(80, this.width * 0.2);
        if (this.deltaX > threshold) this.prev();
        else if (this.deltaX < -threshold) this.next();
        else this._translateTo(this._i);
        this.dragging = false;
      };

      el.addEventListener('touchstart', onStart, { passive: true });
      el.addEventListener('touchmove',  onMove,  { passive: false });
      el.addEventListener('touchend',   onEnd,   { passive: true });

      // desktop drag fallback
      el.addEventListener('mousedown', onStart);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
    }
  }

  /* ============== InnerZoom (Desktop only) ============== */
  class InnerZoom {
    constructor(wrapper, img) {
      this.wrap = wrapper; this.img = img;
      this.active = false; this.scale = 1; this.tx = 0; this.ty = 0;
      this._pendingZoom = false;
      this._bind();
    }
    _bind() {
      this.wrap.addEventListener('mouseenter', () => { if (isDesktop()) this._pendingZoom = true; });
      this.wrap.addEventListener('mouseleave', () => { if (isDesktop()) this.deactivate(); });
      this.wrap.addEventListener('mousemove', (e) => {
        if (!isDesktop()) return;
        if (this._pendingZoom) { this.activate(2); this._pendingZoom = false; }
        this._onMouseMove(e);
      });
    }
    activate(scale) { this.active = true; this.scale = scale; this.wrap.setAttribute('data-zooming','true'); this._apply(); }
    deactivate() { this.active = false; this.scale = 1; this.tx = 0; this.ty = 0; this.wrap.removeAttribute('data-zooming'); this._apply(); }
    _onMouseMove(e) {
      if (!this.active || !isDesktop()) return;
      const rect = this.wrap.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const renderW = this.img.clientWidth, renderH = this.img.clientHeight;
      const maxTx = Math.max(0, renderW * this.scale - rect.width);
      const maxTy = Math.max(0, renderH * this.scale - rect.height);
      this.tx = -maxTx * x; this.ty = -maxTy * y; this._apply();
    }
    _apply() {
      this.img.style.transformOrigin = '0 0';
      this.img.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
    }
  }

  /* ============== Controller ============== */
  class Controller {
    constructor(root) {
      this.root = root;
      this.sliderEl = sel(root, '[data-slider]');
      this.trackEl = sel(root,  '[data-track]');
      this.plpPrev  = sel(root, '[data-plp-prev]');
      this.plpNext  = sel(root, '[data-plp-next]');
      this.plpThumbsTrack = sel(root, '[data-plp-thumbs-track]');
      this.dotsEl   = sel(root, '[data-dots]');

      this.modal         = sel(root, '[data-modal]');
      this.modalSliderEl = sel(root, '[data-modal-slider]');
      this.modalTrackEl  = sel(root, '[data-modal-track]');
      this.modalThumbsTrack = sel(root, '[data-thumbs-track]');
      this.thumbsViewport   = sel(this.modal, '.cpg-thumbs-viewport');
      this.modalPrev = sel(this.modalSliderEl, '[data-modal-prev]');
      this.modalNext = sel(this.modalSliderEl, '[data-modal-next]');
      this.modalClose = sel(root, '[data-close-modal]');
      this.overlay    = sel(this.modal, '[data-close-overlay]');
      this.sideLeft   = sel(this.modal, '[data-close-left]');
      this.sideRight  = sel(this.modal, '[data-close-right]');
      this.thumbsPrev = sel(root, '[data-thumbs-prev]');
      this.thumbsNext = sel(root, '[data-thumbs-next]');

      // Enable loop on both sliders
      this.plp        = new Slider(this.sliderEl,      '[data-track]',       { loop: true });
      this.modalSlider= new Slider(this.modalSliderEl, '[data-modal-track]', { loop: true });

      this._bind();
      this._setupZoom();
      this._buildDots();
      this._syncUI(0);
      this._initThumbsScrollState();
      this._initInitialVariant(); // initial only, ongoing handled by File 2
    }

    _bind() {
      this.sliderEl.addEventListener('click', (e) => {
        if (e.target.closest('.cpg-plp-thumb') || e.target.closest('.cpg-arrow')) return;
        this.openModal(this.plp.index);
      });
      if (this.plpPrev) this.plpPrev.addEventListener('click',  (e)=>{ e.stopPropagation(); this.plp.prev(); });
      if (this.plpNext) this.plpNext.addEventListener('click',  (e)=>{ e.stopPropagation(); this.plp.next(); });
      selAll(this.plpThumbsTrack, '.cpg-plp-thumb').forEach((t,i) => {
        t.addEventListener('click', (e)=>{ e.stopPropagation(); this.plp.goTo(i); });
      });

      if (this.modalPrev) this.modalPrev.addEventListener('click', (e)=>{ e.stopPropagation(); this.modalSlider.prev(); });
      if (this.modalNext) this.modalNext.addEventListener('click', (e)=>{ e.stopPropagation(); this.modalSlider.next(); });

      selAll(this.modalThumbsTrack, '.cpg-thumb').forEach((t,i) => {
        t.addEventListener('click', (e)=>{ e.stopPropagation(); this.modalSlider.goTo(i); });
      });

      if (this.thumbsPrev) this.thumbsPrev.addEventListener('click', (e)=>{ e.stopPropagation(); this._scrollThumbs(-1); });
      if (this.thumbsNext) this.thumbsNext.addEventListener('click', (e)=>{ e.stopPropagation(); this._scrollThumbs(1); });
      if (this.thumbsViewport) {
        this.thumbsViewport.addEventListener('scroll', ()=> this._updateThumbsArrowState(), { passive: true });
        window.addEventListener('resize', ()=> this._updateThumbsArrowState(), { passive: true });
      }

      if (this.modalClose) this.modalClose.addEventListener('click', () => this.closeModal());
      if (this.overlay)    this.overlay.addEventListener('click',    () => this.closeModal());
      if (this.sideLeft)   this.sideLeft.addEventListener('click',   () => this.closeModal());
      if (this.sideRight)  this.sideRight.addEventListener('click',  () => this.closeModal());
      this.modal.addEventListener('click', (e) => {
        const inActive =
          e.target.closest('[data-modal-slider]') ||
          e.target.closest('.cpg-thumbs') ||
          e.target.closest('[data-modal-prev]') ||
          e.target.closest('[data-modal-next]') ||
          e.target.closest('[data-close-modal]');
        if (!inActive) this.closeModal();
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isModalOpen()) return;
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowLeft') this.modalSlider.prev();
        if (e.key === 'ArrowRight') this.modalSlider.next();
      });

      this.plp.onChange        = (i) => this._syncUI(i);
      this.modalSlider.onChange= (i) => this._syncUI(i, true);
    }

    _setupZoom() {
      selAll(this.root, '.cpg-img-wrap').forEach(wrap => {
        const img = sel(wrap, 'img');
        if (img) new InnerZoom(wrap, img);
      });
    }

    _buildDots() {
      if (!this.dotsEl) return;
      this.dotsEl.innerHTML = '';
      // count only real slides, ignore clones
      const count = selAll(this.trackEl, ':scope > *:not([data-clone="true"])').length;
      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.className = 'cpg-dot';
        b.type = 'button';
        b.setAttribute('aria-label', `Go to image ${i+1}`);
        b.addEventListener('click', (e) => { e.stopPropagation(); this.plp.goTo(i); });
        this.dotsEl.appendChild(b);
      }
    }

    _syncUI(i, modal = false) {
      if (modal) {
        selAll(this.modalThumbsTrack, '.cpg-thumb').forEach((t, idx) => {
          t.classList.toggle('is-active', idx === i);
        });
        const active = sel(this.modalThumbsTrack, '.cpg-thumb.is-active');
        if (active && this.thumbsViewport) {
          const ar = active.getBoundingClientRect();
          const vr = this.thumbsViewport.getBoundingClientRect();
          if (ar.left < vr.left)  this.thumbsViewport.scrollBy({ left: ar.left - vr.left - 12, behavior: 'smooth' });
          if (ar.right > vr.right) this.thumbsViewport.scrollBy({ left: ar.right - vr.right + 12, behavior: 'smooth' });
        }
      } else {
        const dots = selAll(this.dotsEl || document, '.cpg-dot');
        dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
        selAll(this.plpThumbsTrack, '.cpg-plp-thumb').forEach((t, idx) => {
          t.classList.toggle('is-active', idx === i);
        });
      }
    }

    _initThumbsScrollState() { requestAnimationFrame(()=> this._updateThumbsArrowState()); }
    _updateThumbsArrowState() {
      if (!this.thumbsViewport) return;
      const maxScroll = this.thumbsViewport.scrollWidth - this.thumbsViewport.clientWidth;
      const atStart = this.thumbsViewport.scrollLeft <= 1 || maxScroll <= 0;
      const atEnd = this.thumbsViewport.scrollLeft >= maxScroll - 1 || maxScroll <= 0;
      this._toggleDisabled(this.thumbsPrev, atStart);
      this._toggleDisabled(this.thumbsNext, atEnd);
    }
    _toggleDisabled(btn, state) {
      if (!btn) return;
      btn.disabled = !!state;
      btn.setAttribute('aria-disabled', state ? 'true' : 'false');
      btn.style.opacity = state ? '0.4' : '1';
      btn.style.cursor = state ? 'default' : 'pointer';
    }
    _scrollThumbs(direction) {
      if (!this.thumbsViewport) return;
      const step = Math.max(160, Math.floor(this.thumbsViewport.clientWidth * 0.6));
      this.thumbsViewport.scrollBy({ left: direction * step, behavior: 'smooth' });
      setTimeout(()=> this._updateThumbsArrowState(), 220);
    }

    /* ---------- Variant init only ---------- */
    _initInitialVariant() {
      const initialId = this.root.getAttribute('data-initial-media-id');
      if (!initialId) return;
      this.setByVariantMediaId(initialId, false);
    }

    _findLogicalIndexByMediaId(trackEl, idStr) {
      if (!trackEl) return -1;
      const slides = selAll(trackEl, ':scope > *:not([data-clone="true"])');
      for (let i = 0; i < slides.length; i++) {
        const slideId = slides[i].getAttribute('data-media-id');
        if (slideId && String(slideId) === String(idStr)) return i;
      }
      return -1;
    }

    setByVariantMediaId(mediaId, animate = true) {
      const idStr = String(mediaId);
      const plpLi = this._findLogicalIndexByMediaId(this.trackEl, idStr);
      if (plpLi > -1) this.plp.goTo(plpLi, animate);

      const modalLi = this._findLogicalIndexByMediaId(this.modalTrackEl, idStr);
      if (modalLi > -1) this.modalSlider.goTo(modalLi, animate);

      this._syncUI(plpLi > -1 ? plpLi : this.plp.index);
      this._syncUI(modalLi > -1 ? modalLi : this.modalSlider.index, true);
    }

    /* ---------- Modal ---------- */
    openModal(startIndex = 0) {
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.style.pointerEvents = 'auto';
      document.documentElement.style.overflow = 'hidden';
      this.modalSlider.goTo(startIndex, false);
      selAll(this.modal, '.cpg-img-wrap img').forEach(img => {
        img.style.transform = 'translate3d(0,0,0) scale(1)';
        img.parentElement.removeAttribute('data-zooming');
      });
      this._initThumbsScrollState();
    }
    closeModal() {
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.style.pointerEvents = 'none';
      document.documentElement.style.overflow = '';
    }
    isModalOpen() { return this.modal.getAttribute('aria-hidden') === 'false'; }
  }

  function init() {
    const root = document.getElementById('custom-product-gallery');
    if (!root) return;
    new Controller(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
